import type { SoundVoice } from "./contracts";

export type VoiceStatus =
  | "live"
  | "needs_attention"
  | "pending_approval"
  | "turned_off"
  | "not_in_product";

export const voiceStatusLabels: Record<VoiceStatus, string> = {
  live: "Live",
  needs_attention: "Needs attention",
  pending_approval: "Pending approval",
  turned_off: "Turned off",
  not_in_product: "Not in product",
};

const statusOrder: VoiceStatus[] = [
  "needs_attention",
  "live",
  "pending_approval",
  "turned_off",
  "not_in_product",
];

export function voiceStatus(voice: SoundVoice): VoiceStatus {
  if (voice.catalog_state === "uncurated") return "not_in_product";
  if (voice.catalog_state === "disabled") return "turned_off";
  if (voice.catalog_state === "draft") return "pending_approval";
  if (
    voice.catalog_state === "orphaned" ||
    voice.provider_state === "missing" ||
    voice.provider_state === "unavailable" ||
    voice.provider_state === "disabled_soon"
  ) {
    return "needs_attention";
  }
  return "live";
}

export function voiceStatusHint(voice: SoundVoice): string | null {
  if (voiceStatus(voice) !== "needs_attention") return null;
  if (voice.provider_state === "disabled_soon") {
    return "ElevenLabs will disable this voice soon.";
  }
  return null;
}

export type SoundFilters = {
  search: string;
  status: VoiceStatus | "all";
  gender: string;
  accent: string;
};

export const initialSoundFilters: SoundFilters = {
  search: "",
  status: "all",
  gender: "all",
  accent: "all",
};

export function hasActiveSoundFilters(filters: SoundFilters) {
  return (
    filters.search.trim() !== "" ||
    filters.status !== "all" ||
    filters.gender !== "all" ||
    filters.accent !== "all"
  );
}

export function voiceGender(voice: SoundVoice) {
  return (
    voice.catalog?.normalized_profile.gender ?? voice.labels.gender ?? null
  );
}

export function voiceAccent(voice: SoundVoice) {
  return (
    voice.catalog?.normalized_profile.accent ?? voice.labels.accent ?? null
  );
}

export function filterSoundVoices(voices: SoundVoice[], filters: SoundFilters) {
  const search = filters.search.trim().toLocaleLowerCase();
  return voices.filter((voice) => {
    const name = voice.catalog?.display_name ?? voice.name;
    if (
      search &&
      !name.toLocaleLowerCase().includes(search) &&
      !voice.voice_id.toLocaleLowerCase().includes(search)
    ) {
      return false;
    }
    if (filters.status !== "all" && voiceStatus(voice) !== filters.status) {
      return false;
    }
    if (filters.gender !== "all" && voiceGender(voice) !== filters.gender) {
      return false;
    }
    if (filters.accent !== "all" && voiceAccent(voice) !== filters.accent) {
      return false;
    }
    return true;
  });
}

export function sortSoundVoices(voices: SoundVoice[]) {
  return [...voices].sort((left, right) => {
    const byStatus =
      statusOrder.indexOf(voiceStatus(left)) -
      statusOrder.indexOf(voiceStatus(right));
    if (byStatus !== 0) return byStatus;
    const byUsage =
      (right.usage?.selection_count ?? 0) - (left.usage?.selection_count ?? 0);
    if (byUsage !== 0) return byUsage;
    const leftName = left.catalog?.display_name ?? left.name;
    const rightName = right.catalog?.display_name ?? right.name;
    return leftName.localeCompare(rightName);
  });
}

export function distinctVoiceValues(
  voices: SoundVoice[],
  selector: (voice: SoundVoice) => string | null | undefined,
) {
  return Array.from(
    new Set(voices.map(selector).filter((value): value is string => !!value)),
  ).sort((left, right) => left.localeCompare(right));
}
